import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError, fmtDate } from "@/lib/api";
import { AbsenceForm } from "@/components/AbsenceForm";
import { DayAttendanceEditor } from "@/components/timesheet/DayAttendanceEditor";

const DayAbsences = ({ operatorId, day, types, onChanged }) => {
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const remove = async (id) => {
    try {
      await api.delete(`/absences/${id}`);
      toast.success("Assenza eliminata");
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };
  const done = () => { setEditing(null); setAdding(false); onChanged(); };
  return (
    <div className="space-y-3" data-testid="day-absence-editor">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Assenze / causali</h3>
        {!adding && <button data-testid="day-abs-add-toggle" onClick={() => setAdding(true)} className="text-xs font-semibold text-emerald-700 flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Aggiungi assenza</button>}
      </div>
      {day.absences.length === 0 && !adding && <p className="text-xs text-gray-500">Nessuna assenza registrata.</p>}
      {day.absences.map((a) => editing === a.id ? (
        <AbsenceForm key={a.id} types={types} operatorId={operatorId} initial={a} onSaved={done} onCancel={() => setEditing(null)} testPrefix={`abs-edit-${a.id}`} />
      ) : (
        <div key={a.id} data-testid={`day-abs-${a.id}`} className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-semibold">{a.label}</span>
          <span className="text-gray-600">{fmtDate(a.start_date)} – {fmtDate(a.end_date)}</span>
          {a.hours ? <span className="font-mono-num text-gray-600">{a.hours}h</span> : null}
          {a.reason && <span className="text-gray-500 text-xs">{a.reason}</span>}
          <span className="ml-auto flex gap-2">
            <button data-testid={`day-abs-edit-${a.id}`} onClick={() => setEditing(a.id)} aria-label="Modifica assenza" className="h-8 w-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50"><Pencil className="h-3.5 w-3.5" /></button>
            <button data-testid={`day-abs-delete-${a.id}`} onClick={() => remove(a.id)} aria-label="Elimina assenza" className="h-8 w-8 rounded-lg border border-red-200 text-red-600 flex items-center justify-center hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
          </span>
        </div>
      ))}
      {adding && <AbsenceForm types={types} operatorId={operatorId} initial={{ start_date: day.date, end_date: day.date }} onSaved={done} onCancel={() => setAdding(false)} testPrefix="abs-new" />}
    </div>
  );
};

const DayNote = ({ operatorId, day, onChanged }) => {
  const [note, setNote] = useState(day.note || "");
  useEffect(() => { setNote(day.note || ""); }, [day.note]);
  const save = async () => {
    try {
      await api.put("/reports/day-note", { operator_id: operatorId, date: day.date, note });
      toast.success("Nota salvata");
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold">Nota del titolare</h3>
      <textarea data-testid="day-note-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={500}
        className="w-full rounded-lg border border-gray-200 p-3 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none" placeholder="Es. uscita anticipata autorizzata" />
      <div className="flex justify-end"><button data-testid="day-note-save" onClick={save} className="h-9 px-4 rounded-lg bg-gray-900 text-white text-xs font-semibold">Salva nota</button></div>
    </div>
  );
};

export const DayEditorDialog = ({ target, types, onClose, onChanged }) => {
  const op = target?.op;
  const day = target?.day;
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="day-editor-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto">
        {day && (
          <>
            <DialogHeader>
              <DialogTitle className="font-head">{op.operator_name} · {day.weekday_label} {fmtDate(day.date)}</DialogTitle>
              <DialogDescription>
                {day.is_holiday ? `Festivo: ${day.holiday_name}. ` : day.is_sunday ? "Domenica. " : ""}
                Aggiungi, correggi o elimina timbrature, causali di assenza e note. Ogni modifica è tracciata nel Registro modifiche.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 pt-2">
              <DayAttendanceEditor operatorId={op.operator_id} day={day} onChanged={onChanged} />
              <DayAbsences operatorId={op.operator_id} day={day} types={types} onChanged={onChanged} />
              <DayNote operatorId={op.operator_id} day={day} onChanged={onChanged} />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
